import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search } from 'lucide-react';
import { SPEECHMATICS_LANGUAGES, type Language } from '../../shared/constants/languages';

interface LanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (language: string) => void;
  disabled?: boolean;
}

const languages = SPEECHMATICS_LANGUAGES;

const LanguageSelector: React.FC<LanguageSelectorProps> = React.memo(({ 
  selectedLanguage, 
  onLanguageChange, 
  disabled = false 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const currentLanguage = languages.find(lang => lang.code === selectedLanguage) || languages[0];
  
  // Fuzzy search function
  const fuzzySearch = (query: string, text: string): boolean => {
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    
    // Direct substring match
    if (textLower.includes(queryLower)) return true;
    
    // Fuzzy matching - allow characters to be non-consecutive
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  };
  
  // Filter languages based on search
  const filteredLanguages = searchQuery
    ? languages.filter(lang => 
        fuzzySearch(searchQuery, lang.name) || 
        fuzzySearch(searchQuery, lang.native) ||
        fuzzySearch(searchQuery, lang.code)
      )
    : languages;
  
  // Calculate dropdown position and focus search when it opens
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 8, // Position above the button with some margin
        left: rect.left,
      });
      // Focus search input after a brief delay for animation
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Clear search when closing
      setSearchQuery('');
    }
  }, [showDropdown]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);
  
  const handleLanguageSelect = (langCode: string) => {
    onLanguageChange(langCode);
    setShowDropdown(false);
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredLanguages.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredLanguages.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredLanguages[highlightedIndex]) {
          handleLanguageSelect(filteredLanguages[highlightedIndex].code);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        break;
    }
  };
  
  // Reset highlighted index when search changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery]);
  
  return (
    <>
      <motion.button
        ref={buttonRef}
        whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
        whileTap={{ scale: 0.98 }}
        onClick={(e) => {
          e.stopPropagation();
          setShowDropdown(!showDropdown);
        }}
        disabled={disabled}
        className={`px-4 py-4 flex items-center space-x-2.5 transition-all border-r ${
          showDropdown ? 'bg-gray-50' : 'bg-white'
        } ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
        } border-gray-100`}
      >
        <span className="text-xl">{currentLanguage.flag}</span>
        <div className="flex flex-col items-start">
          <span className="text-xs font-medium text-gray-900">{currentLanguage.name}</span>
          <span className="text-[10px] text-gray-500">{currentLanguage.native}</span>
        </div>
        <motion.div
          animate={{ rotate: showDropdown ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
        </motion.div>
      </motion.button>
      
      {/* Language Dropdown - Rendered with Portal */}
      {showDropdown && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, type: "spring", stiffness: 300, damping: 30 }}
            className="fixed w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 z-[9999]"
            style={{
              top: `${dropdownPosition.top - 500}px`, // Position above button with space for search + full height
              left: `${dropdownPosition.left}px`,
              maxHeight: '500px',
              boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* Search Input */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={2} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search languages..."
                  className="w-full pl-10 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-sky-500 focus:bg-white transition-all"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            
            {/* Languages List */}
            <div className="max-h-[430px] overflow-y-auto">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map((lang, index) => (
                <motion.button
                  key={lang.code}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageSelect(lang.code);
                  }}
                  className={`w-full px-4 py-3 flex items-center space-x-3 transition-all ${
                    selectedLanguage === lang.code
                      ? 'bg-sky-50 border-l-2 border-sky-500'
                      : highlightedIndex === index
                      ? 'bg-gray-100 border-l-2 border-gray-300'
                      : 'hover:bg-gray-50 border-l-2 border-transparent'
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-900">{lang.name}</div>
                    <div className="text-xs text-gray-500">{lang.native}</div>
                  </div>
                  {selectedLanguage === lang.code && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="w-1.5 h-1.5 rounded-full bg-sky-500"
                    />
                  )}
                </motion.button>
              ))
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-4 py-8 text-center"
                >
                  <div className="text-gray-400 mb-2">
                    <Search className="w-8 h-8 mx-auto" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-gray-500">No languages found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
});

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;